import { useMemo } from 'react';
import { Flex, ResponsiveValue } from '@chakra-ui/react';

import {
	useFilters,
	styles,
	useCalendarSettings,
	useShortcodeSettings,
	useLocationFilterStates,
} from '../../../../../lib';
import { SearchFilter } from '../';
import { UnifiedFilterPill } from './UnifiedFilterPill';
import { __ } from '../../../../../lib/i18n';

export const RenderHorizontalFilters = () => {
	const { filters, defaultFilters, handleApplyFilters } = useFilters();
	const { settings } = useCalendarSettings();
	const { settings: shortcodeSettings } = useShortcodeSettings();

	const { enableSearchFilter, enableLocationFilter, enableCategoryFilter, enableVenueFilter, enableTagFilter } =
		settings;
	const showFilters = shortcodeSettings.filters;
	const { countries, states, cities } = shortcodeSettings;

	const [showCountryFilter, showStateFilter, showCityFilter] = useMemo(
		() => [!countries && !states && !cities, !states && !cities, !cities],
		[countries, states, cities]
	);

	const { isCitiesFilterDisabled, isStatesFilterDisabled } = useLocationFilterStates();

	const filterConfigs = useMemo(
		() => [
			{
				filterKey: 'selectedEventTypes',
				label: settings.categoryLabel,
				options: defaultFilters.eventTypes,
				selectedOptions: filters.selectedEventTypes,
				isMulti: true,
				showColors: true,
				isEnabled: !shortcodeSettings.cat && (showFilters || enableCategoryFilter),
			},
			{
				filterKey: 'selectedEventLocations',
				label: settings.venueLabel,
				options: defaultFilters.eventLocations,
				selectedOptions: filters.selectedEventLocations,
				isMulti: true,
				isEnabled: !shortcodeSettings.venue && (showFilters || enableVenueFilter),
			},
			{
				filterKey: 'selectedEventTags',
				label: settings.tagLabel,
				options: defaultFilters.eventTags,
				selectedOptions: filters.selectedEventTags,
				isMulti: true,
				isEnabled: !shortcodeSettings.tag && (showFilters || enableTagFilter),
			},
			{
				filterKey: 'selectedCountries',
				label: __('Country'),
				options: defaultFilters.countries,
				selectedOptions: filters.selectedCountries,
				isMulti: false,
				isEnabled: (showFilters || enableLocationFilter) && showCountryFilter,
			},
			{
				filterKey: 'selectedStates',
				label: __('State'),
				options: defaultFilters.states,
				selectedOptions: filters.selectedStates,
				isMulti: false,
				disabled: isStatesFilterDisabled,
				isEnabled: (showFilters || enableLocationFilter) && showStateFilter,
			},
			{
				filterKey: 'selectedCities',
				label: __('City'),
				options: defaultFilters.cities,
				selectedOptions: filters.selectedCities,
				isMulti: false,
				disabled: isCitiesFilterDisabled,
				isEnabled: (showFilters || enableLocationFilter) && showCityFilter,
			},
		],
		[
			filters,
			defaultFilters,
			showFilters,
			enableLocationFilter,
			enableTagFilter,
			enableVenueFilter,
			enableCategoryFilter,
			isStatesFilterDisabled,
			isCitiesFilterDisabled,
			showCountryFilter,
			showStateFilter,
			showCityFilter,
			shortcodeSettings.cat,
			shortcodeSettings.venue,
			shortcodeSettings.tag,
			settings.categoryLabel,
			settings.venueLabel,
			settings.tagLabel,
		]
	);

	const { filterPillsWrap } = styles.filters;
	return (
		<Flex direction='column' gap={4} w='full' paddingBlock={4}>
			{(showFilters || enableSearchFilter) && <SearchFilter onApplyFilters={handleApplyFilters} />}
			<Flex gap={2} flexWrap={filterPillsWrap as ResponsiveValue<'wrap' | 'nowrap'>}>
				{filterConfigs
					.filter((filter) => filter.isEnabled)
					.map((config) => (
						<UnifiedFilterPill key={config.filterKey} {...config} onApplyFilters={handleApplyFilters} />
					))}
			</Flex>
		</Flex>
	);
};
